import { makeStyles } from "@fluentui/react-components";
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { getQueries } from "@/api/queries";
import type { CardData } from "@/types";
import type { OfficeSelection } from "@/types/api";
import Card from "@/components/cards";
import QuestionSubmit from "./question-submit";
import QuestionsList from "./questions-list";

interface HomeTabProps {
  searchQuery: string;
  selection: OfficeSelection | null;
  performanceAnswers: CardData[];
}

function HomeTab({ searchQuery, selection, performanceAnswers }: HomeTabProps) {
  const styles = useStyles();
  const context = selection?.context;
  const isDueDiligence = context?.requestType === "due-diligence";

  const { data: queries = [] } = useQuery({
    queryKey: [
      "queries",
      {
        organizationId: selection?.organizationId ?? "",
        productId: isDueDiligence ? context.productId : "",
        requestId: isDueDiligence ? context.requestId : "",
      },
    ],
    queryFn: getQueries,
    enabled: isDueDiligence,
  });

  const filteredQuestions = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return queries;
    }

    const query = searchQuery.toLowerCase();
    return queries.filter((q) => (q.question || "").toLowerCase().includes(query));
  }, [searchQuery, queries]);

  const filteredPerformanceAnswers = performanceAnswers.filter((item) =>
    item.question.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  if (!selection) return <p className={styles.hint}>Complete the choices above to use worksheet formulas.</p>;
  if (!isDueDiligence)
    return (
      <div className={styles.container} role="tabpanel" aria-labelledby="Home">
        <p className={styles.hint}>
          Performance answers from formulas in this session appear here with their report sources.
        </p>
        {!!searchQuery && <p className={styles.hint}>{filteredPerformanceAnswers.length} matching answers</p>}
        {filteredPerformanceAnswers.map((answer) => (
          <Card key={answer.id} data={answer} variant="chat" />
        ))}
      </div>
    );

  return (
    <div className={styles.container} role="tabpanel" aria-labelledby="Home">
      {searchQuery && (
        <div className={styles.searchResultsText}>
          {filteredQuestions.length === 0
            ? "No questions found"
            : `${filteredQuestions.length} question${filteredQuestions.length !== 1 ? "s" : ""} found`}
        </div>
      )}

      <div className={styles.homeContent}>
        <QuestionsList questionItems={filteredQuestions} />
        <QuestionSubmit />
      </div>
    </div>
  );
}

export default React.memo(HomeTab);

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    gap: "12px",
    paddingTop: "12px",
    overflowY: "auto",
  },
  hint: { fontSize: "12px", lineHeight: "18px", color: "#606e8c" },
  searchResultsText: {
    fontSize: "12px",
    color: "#606e8c",
    paddingLeft: "4px",
  },
  homeContent: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
  },
});
